/**
 * TenantMigrationModal
 *
 * Admin UI for migrating a legacy tenant to the new Billing BC.
 *
 * Flow:
 * 1. Loading state — calls GET /admin/billing/migrate-tenant/:tenantId/preview
 *    (may take up to 30s due to GUS + Fakturownia)
 * 2. Preview state — shows company data, subscriptions, documents, warnings
 *    Blocking reasons shown if canMigrate=false (button disabled)
 * 3. Confirming state — POST /admin/billing/migrate-tenant/:tenantId in progress
 * 4. Result state — shows success summary: subscriptions, documents, warnings
 *
 * @see AC-A6, AC-A7 in docs/specifications/2026-03-26-billing-tenant-migration.md
 */
import React, { useEffect, useRef, useState } from "react";
import {
  useMigrationPreviewQuery,
  useExecuteTenantMigrationMutation,
  type MigrationPreviewDto,
  type MigrationResultDto,
} from "../../../../hooks/useAdminProductCatalog";

// =============================================================================
// TYPES
// =============================================================================

interface TenantMigrationModalProps {
  tenantId: string;
  tenantName: string;
  onClose: () => void;
  onMigrationComplete?: () => void;
}

// =============================================================================
// HELPERS
// =============================================================================

const formatDate = (isoString: string | null): string => {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("pl-PL", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDateTime = (isoString: string): string => {
  return new Date(isoString).toLocaleString("pl-PL", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    currencyDisplay: "code",
    maximumFractionDigits: 0,
  }).format(amount);
};

const mapOldStatus = (status: string): string => {
  const labels: Record<string, string> = {
    active: "aktywna",
    trialing: "trial",
    canceled: "anulowana",
    past_due: "zaległa",
    unpaid: "niezapłacona",
    suspended: "zawieszona",
    incomplete: "niekompletna",
  };
  return labels[status] ?? status;
};

const mapNewStatus = (status: string): string => {
  const labels: Record<string, string> = {
    ACTIVE: "AKTYWNA",
    TRIALING: "TRIAL",
    CANCELED: "ANULOWANA",
    EXPIRED: "WYGASŁA",
  };
  return labels[status] ?? status;
};

const getNewStatusBadgeClass = (status: string): string => {
  switch (status) {
    case "ACTIVE":
      return "bg-green-100 text-green-800";
    case "TRIALING":
      return "bg-blue-100 text-blue-800";
    case "CANCELED":
      return "bg-red-100 text-red-800";
    case "EXPIRED":
      return "bg-neutral-100 text-neutral-600";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

// =============================================================================
// LOADING STATE (AC-A6)
// =============================================================================

const LoadingState: React.FC = () => (
  <div className="p-8 flex flex-col items-center gap-4">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
    <div className="text-center">
      <p className="text-neutral-800 font-medium">Pobieranie podglądu migracji...</p>
      <p className="text-neutral-500 text-sm mt-1">
        Operacja może trwać do 30s (GUS + Fakturownia)
      </p>
    </div>
  </div>
);

// =============================================================================
// PREVIEW STATE (AC-A6)
// =============================================================================

interface PreviewStateProps {
  preview: MigrationPreviewDto;
  isConfirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const PreviewState: React.FC<PreviewStateProps> = ({
  preview,
  isConfirming,
  onConfirm,
  onCancel,
}) => {
  return (
    <div className="space-y-5">
      {/* Blocking reasons (if any) */}
      {preview.blockingReasons.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h4 className="text-sm font-semibold text-red-800 mb-2">
            Migracja zablokowana
          </h4>
          <ul className="list-disc list-inside space-y-1">
            {preview.blockingReasons.map((reason, i) => (
              <li key={i} className="text-sm text-red-700">
                {reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Non-blocking warnings */}
      {preview.warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <h4 className="text-sm font-semibold text-amber-800 mb-2">
            Ostrzeżenia
          </h4>
          <ul className="list-disc list-inside space-y-1">
            {preview.warnings.map((w, i) => (
              <li key={i} className="text-sm text-amber-700">
                {w}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Company data from GUS */}
      <div className="bg-neutral-50 rounded-md p-4 space-y-2">
        <h4 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide">
          Dane firmy (GUS)
        </h4>
        {preview.companyData ? (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <dt className="text-neutral-500">Nazwa</dt>
            <dd className="text-neutral-900 font-medium">{preview.companyData.name}</dd>
            <dt className="text-neutral-500">NIP</dt>
            <dd className="text-neutral-900 font-mono">{preview.nip}</dd>
            <dt className="text-neutral-500">Ulica</dt>
            <dd className="text-neutral-900">{preview.companyData.street}</dd>
            <dt className="text-neutral-500">Miasto</dt>
            <dd className="text-neutral-900">
              {preview.companyData.postalCode} {preview.companyData.city}
            </dd>
          </dl>
        ) : (
          <p className="text-sm text-neutral-500 italic">
            Dane GUS niedostępne (NIP: {preview.nip || "brak"})
          </p>
        )}
      </div>

      {/* Subscriptions */}
      <div>
        <h4 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide mb-2">
          Subskrypcje do migracji ({preview.subscriptions.length})
        </h4>
        {preview.subscriptions.length === 0 ? (
          <p className="text-sm text-neutral-500 italic">Brak subskrypcji</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                    Stary status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                    Nowy status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                    Okres rozliczeniowy
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                    Koniec okresu
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {preview.subscriptions.map((sub) => (
                  <tr key={sub.id} className="hover:bg-neutral-50">
                    <td className="px-3 py-2 text-neutral-600">
                      {mapOldStatus(sub.oldStatus)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getNewStatusBadgeClass(sub.newStatus)}`}
                      >
                        {mapNewStatus(sub.newStatus)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-neutral-600">
                      {sub.billingPeriod ?? (
                        <span className="text-red-600 font-medium">BRAK</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-neutral-600">
                      {formatDate(sub.currentPeriodEnd)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Documents */}
      <div>
        <h4 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide mb-2">
          Dokumentów do importu ({preview.documents.length})
        </h4>
        {preview.documents.length === 0 ? (
          <p className="text-sm text-neutral-500 italic">
            Brak dokumentów do importu
          </p>
        ) : (
          <div className="overflow-x-auto max-h-48 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-100 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                    Typ
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                    Numer
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                    Kwota brutto
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                    Data wystawienia
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {preview.documents.map((doc) => (
                  <tr key={doc.number} className="hover:bg-neutral-50">
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          doc.type === "VAT_INVOICE"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-purple-100 text-purple-800"
                        }`}
                      >
                        {doc.type === "VAT_INVOICE" ? "Faktura VAT" : "Proforma"}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-neutral-700">
                      {doc.number}
                    </td>
                    <td className="px-3 py-2 text-neutral-700">
                      {formatCurrency(doc.amountGross)}
                    </td>
                    <td className="px-3 py-2 text-neutral-600">
                      {formatDate(doc.issueDate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2 border-t border-neutral-200">
        <button
          onClick={onCancel}
          disabled={isConfirming}
          className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-800 font-medium disabled:opacity-50"
        >
          Anuluj
        </button>
        <button
          onClick={onConfirm}
          disabled={!preview.canMigrate || isConfirming}
          className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          title={
            !preview.canMigrate
              ? "Migracja zablokowana — sprawdź przyczyny powyżej"
              : undefined
          }
        >
          {isConfirming ? "Migrowanie..." : "Potwierdź migrację"}
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// RESULT STATE (AC-A7)
// =============================================================================

interface ResultStateProps {
  result: MigrationResultDto;
  onClose: () => void;
}

const ResultState: React.FC<ResultStateProps> = ({ result, onClose }) => {
  return (
    <div className="space-y-5">
      {/* Success header */}
      <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-md p-4">
        <svg
          className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div>
          <p className="text-sm font-semibold text-green-800">
            Migracja zakończona pomyślnie
          </p>
          <p className="text-xs text-green-700 mt-0.5">
            {formatDateTime(result.migratedAt)}
          </p>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-neutral-50 rounded-md p-3 text-center">
          <div className="text-2xl font-bold text-neutral-800">
            {result.subscriptionsMigrated.length}
          </div>
          <div className="text-xs text-neutral-500 mt-0.5">
            Subskrypcji zmigrowanych
          </div>
        </div>
        <div className="bg-neutral-50 rounded-md p-3 text-center">
          <div className="text-2xl font-bold text-neutral-800">
            {result.documentsImported}
          </div>
          <div className="text-xs text-neutral-500 mt-0.5">
            Dokumentów zaimportowanych
          </div>
        </div>
        <div className="bg-neutral-50 rounded-md p-3 text-center">
          <div className="text-2xl font-bold text-neutral-800">
            {result.warnings.length}
          </div>
          <div className="text-xs text-neutral-500 mt-0.5">Ostrzeżeń</div>
        </div>
      </div>

      {/* Migrated subscriptions */}
      {result.subscriptionsMigrated.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide mb-2">
            Zmigrowane subskrypcje
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                    Nowa subskrypcja
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                    Zamówienie
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {result.subscriptionsMigrated.map((sub) => (
                  <tr key={sub.newSubscriptionId}>
                    <td className="px-3 py-2 font-mono text-xs text-neutral-600">
                      {sub.newSubscriptionId.slice(0, 8)}...
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getNewStatusBadgeClass(sub.status)}`}
                      >
                        {mapNewStatus(sub.status)}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-neutral-600">
                      {sub.orderNumber}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Warnings */}
      {result.warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <h4 className="text-sm font-semibold text-amber-800 mb-2">
            Ostrzeżenia (nieblokujące)
          </h4>
          <ul className="list-disc list-inside space-y-1">
            {result.warnings.map((w, i) => (
              <li key={i} className="text-sm text-amber-700">
                {w}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Close button */}
      <div className="flex justify-end pt-2 border-t border-neutral-200">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm bg-neutral-800 text-white rounded-md hover:bg-neutral-900 font-medium"
        >
          Zamknij
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// CONFIRMING STATE (migration POST in progress)
// =============================================================================

const ConfirmingState: React.FC = () => (
  <div className="p-8 flex flex-col items-center gap-4">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
    <div className="text-center">
      <p className="text-neutral-800 font-medium">Trwa migracja danych...</p>
      <p className="text-neutral-500 text-sm mt-1">
        Operacja może potrwać do 30s
      </p>
    </div>
  </div>
);

// =============================================================================
// MIGRATION ERROR STATE (inline error after failed POST)
// =============================================================================

interface MigrationErrorStateProps {
  message: string;
  onRetry: () => void;
  onClose: () => void;
}

const MigrationErrorState: React.FC<MigrationErrorStateProps> = ({
  message,
  onRetry,
  onClose,
}) => (
  <div className="p-6 space-y-4">
    <div className="bg-red-50 border border-red-200 rounded-md p-4">
      <h4 className="text-sm font-semibold text-red-800 mb-1">
        Błąd migracji
      </h4>
      <p className="text-sm text-red-700">{message}</p>
    </div>
    <p className="text-sm text-neutral-600">
      Migracja nie została wykonana. Możesz spróbować ponownie — operacja jest
      idempotentna.
    </p>
    <div className="flex justify-end gap-3">
      <button
        onClick={onClose}
        className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-800 font-medium"
      >
        Zamknij
      </button>
      <button
        onClick={onRetry}
        className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium"
      >
        Spróbuj ponownie
      </button>
    </div>
  </div>
);

// =============================================================================
// ERROR STATE
// =============================================================================

interface ErrorStateProps {
  message: string;
  onRetry: () => void;
  onClose: () => void;
}

const ErrorState: React.FC<ErrorStateProps> = ({ message, onRetry, onClose }) => (
  <div className="p-6 space-y-4">
    <div className="bg-red-50 border border-red-200 rounded-md p-4">
      <h4 className="text-sm font-semibold text-red-800 mb-1">
        Błąd pobierania podglądu
      </h4>
      <p className="text-sm text-red-700">{message}</p>
    </div>
    <div className="flex justify-end gap-3">
      <button
        onClick={onClose}
        className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-800 font-medium"
      >
        Zamknij
      </button>
      <button
        onClick={onRetry}
        className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium"
      >
        Spróbuj ponownie
      </button>
    </div>
  </div>
);

// =============================================================================
// MAIN MODAL (AC-A6, AC-A7)
// =============================================================================

const TenantMigrationModal: React.FC<TenantMigrationModalProps> = ({
  tenantId,
  tenantName,
  onClose,
  onMigrationComplete,
}) => {
  const [migrationResult, setMigrationResult] =
    useState<MigrationResultDto | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const {
    data: preview,
    isLoading: isLoadingPreview,
    isError: isPreviewError,
    error: previewError,
    refetch: refetchPreview,
  } = useMigrationPreviewQuery(tenantId);

  const migrationMutation = useExecuteTenantMigrationMutation();

  // Focus the modal on mount
  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  // Escape to close + focus trap (keeps Tab cycling within modal for VoiceOver/Safari)
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    const focusableQuery = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !migrationMutation.isPending) {
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const focusable = Array.from(modal.querySelectorAll<HTMLElement>(focusableQuery));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, migrationMutation.isPending]);

  const handleConfirm = () => {
    migrationMutation.mutate(tenantId, {
      onSuccess: (result) => {
        setMigrationResult(result);
        onMigrationComplete?.();
      },
    });
  };

  const handleClose = () => {
    if (!migrationMutation.isPending) onClose();
  };

  const getMigrationErrorMessage = (): string => {
    if (!migrationMutation.error) return "Nieznany błąd";
    const err = migrationMutation.error as { response?: { data?: { message?: string } }; message?: string };
    return (
      err.response?.data?.message ||
      err.message ||
      "Migracja nie powiodła się"
    );
  };

  const getTitle = (): string => {
    if (migrationResult) return "Wynik migracji";
    if (migrationMutation.isPending) return "Trwa migracja...";
    if (migrationMutation.isError) return "Błąd migracji";
    if (isLoadingPreview) return "Podgląd migracji";
    return `Migracja tenanta: ${tenantName}`;
  };

  const getErrorMessage = (): string => {
    if (!previewError) return "Nieznany błąd";
    const err = previewError as { response?: { data?: { message?: string } }; message?: string };
    return (
      err.response?.data?.message ||
      err.message ||
      "Nie można pobrać podglądu migracji"
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        tabIndex={-1}
        className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="migration-modal-title"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between flex-shrink-0">
          <h3 id="migration-modal-title" className="text-lg font-semibold text-neutral-900">{getTitle()}</h3>
          <button
            onClick={handleClose}
            disabled={migrationMutation.isPending}
            aria-label="Zamknij"
            className="text-neutral-400 hover:text-neutral-600 disabled:opacity-50"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          {migrationResult ? (
            /* AC-A7: Result */
            <ResultState result={migrationResult} onClose={handleClose} />
          ) : migrationMutation.isPending ? (
            /* Migration POST in progress — dedicated loading state */
            <ConfirmingState />
          ) : migrationMutation.isError ? (
            /* Migration POST failed — inline error with retry */
            <MigrationErrorState
              message={getMigrationErrorMessage()}
              onRetry={handleConfirm}
              onClose={handleClose}
            />
          ) : isLoadingPreview ? (
            /* Loading preview */
            <LoadingState />
          ) : isPreviewError ? (
            /* Preview fetch error */
            <ErrorState
              message={getErrorMessage()}
              onRetry={() => refetchPreview()}
              onClose={handleClose}
            />
          ) : preview ? (
            /* AC-A6: Preview */
            <PreviewState
              preview={preview}
              isConfirming={false}
              onConfirm={handleConfirm}
              onCancel={handleClose}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default TenantMigrationModal;
